import React from "react";
import { format } from "date-fns";
import { CommandXFooter } from "./CommandXFooter";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n || 0);

export interface FinReportBill {
  id: string;
  bill_number: string;
  bill_date: string;
  due_date?: string | null;
  status: string;
  total: number;
  source?: string | null;
  vendor_name?: string | null;
  description?: string | null;
}

export interface FinReportExpense {
  id: string;
  description: string;
  expense_date: string;
  amount: number;
  status: string;
  vendor_name?: string | null;
  qb_expense_id?: string | null;
  bill_id?: string | null;
}

export interface ProjectFinancialReportData {
  company_name?: string;
  company_logo_url?: string | null;
  project_name: string;
  project_number?: string | null;
  customer_name?: string;
  bills: FinReportBill[];
  // Standalone expenses only (bill_id IS NULL) — to avoid double counting.
  standalone_expenses: FinReportExpense[];
  // True if either query was capped at the request limit.
  truncated_bills?: boolean;
  truncated_expenses?: boolean;
  generated_at?: string;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  draft: { bg: "#F3F4F6", color: "#374151" },
  pending: { bg: "#FEF3C7", color: "#92400E" },
  pending_approval: { bg: "#FEF3C7", color: "#92400E" },
  approved: { bg: "#DBEAFE", color: "#1E40AF" },
  paid: { bg: "#D1FAE5", color: "#065F46" },
  void: { bg: "#FEE2E2", color: "#991B1B" },
  rejected: { bg: "#FEE2E2", color: "#991B1B" },
};

const StatusPill = ({ status }: { status: string }) => {
  const s = STATUS_COLORS[status] ?? STATUS_COLORS.draft;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 6px",
        fontSize: 9,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderRadius: 3,
        background: s.bg,
        color: s.color,
      }}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
};

const QbBadge = () => (
  <span
    style={{
      marginLeft: 6,
      padding: "1px 5px",
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: "0.04em",
      border: "1px solid #2563EB",
      color: "#1E40AF",
      borderRadius: 3,
    }}
    title="Imported from QuickBooks"
  >
    QB
  </span>
);

export const ProjectFinancialReportPrintTemplate = React.forwardRef<
  HTMLDivElement,
  { data: ProjectFinancialReportData }
>(({ data }, ref) => {
  // Numeric column style — tabular numerals so columns line up.
  const numCell: React.CSSProperties = {
    textAlign: "right",
    fontFamily:
      "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    padding: "5px 8px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 11,
  };
  const textCell: React.CSSProperties = {
    padding: "5px 8px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 11,
    verticalAlign: "top",
  };
  const headCell: React.CSSProperties = {
    padding: "7px 8px",
    borderBottom: "2px solid #111",
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "#374151",
    background: "#F9FAFB",
  };

  // Group bills by status
  const billsByStatus = data.bills.reduce<Record<string, FinReportBill[]>>(
    (acc, b) => {
      const k = b.status || "draft";
      (acc[k] ||= []).push(b);
      return acc;
    },
    {},
  );
  const billStatusOrder = [
    "draft",
    "pending",
    "pending_approval",
    "approved",
    "paid",
    "void",
    "rejected",
  ].filter((k) => billsByStatus[k]?.length);

  const totalBills = data.bills.reduce((s, b) => s + Number(b.total || 0), 0);
  const totalStandaloneExpenses = data.standalone_expenses.reduce(
    (s, e) => s + Number(e.amount || 0),
    0,
  );
  const combined = totalBills + totalStandaloneExpenses;

  const qbBillsCount = data.bills.filter((b) => b.source === "quickbooks").length;
  const qbExpensesCount = data.standalone_expenses.filter(
    (e) => !!e.qb_expense_id,
  ).length;

  return (
    <div
      ref={ref}
      className="print-template"
      style={{
        padding: 32,
        maxWidth: "8.5in",
        margin: "0 auto",
        fontFamily:
          "ui-sans-serif, system-ui, -apple-system, Segoe UI, sans-serif",
        color: "#111",
        fontSize: 12,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "2px solid #111",
          paddingBottom: 12,
          marginBottom: 16,
        }}
      >
        <div>
          {data.company_logo_url && (
            <img
              src={data.company_logo_url}
              alt={data.company_name ?? "Company"}
              style={{ maxHeight: 40, objectFit: "contain", marginBottom: 4 }}
            />
          )}
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: "-0.01em",
              margin: 0,
            }}
          >
            PROJECT FINANCIAL REPORT
          </h1>
          {data.company_name && (
            <p
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#374151",
                margin: "4px 0 0",
              }}
            >
              {data.company_name}
            </p>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          {data.project_number && (
            <p
              style={{
                fontSize: 16,
                fontWeight: 700,
                fontFamily:
                  "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                margin: 0,
              }}
            >
              {data.project_number}
            </p>
          )}
          <p
            style={{
              fontSize: 10,
              color: "#6B7280",
              margin: "6px 0 0",
            }}
          >
            {format(
              new Date(data.generated_at ?? new Date().toISOString()),
              "MMM d, yyyy 'at' h:mm a",
            )}
          </p>
        </div>
      </div>

      {/* Project meta */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <p
            style={{
              fontSize: 9,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "#9CA3AF",
              fontWeight: 600,
              margin: "0 0 2px",
            }}
          >
            Project
          </p>
          <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>
            {data.project_name}
          </p>
        </div>
        <div>
          <p
            style={{
              fontSize: 9,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "#9CA3AF",
              fontWeight: 600,
              margin: "0 0 2px",
            }}
          >
            Customer
          </p>
          <p style={{ fontSize: 13, fontWeight: 500, margin: 0 }}>
            {data.customer_name || "—"}
          </p>
        </div>
      </div>

      {/* Summary block */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <SummaryCard
          label={`Total Bills (${data.bills.length})`}
          value={fmt(totalBills)}
          note={qbBillsCount ? `${qbBillsCount} from QuickBooks` : undefined}
        />
        <SummaryCard
          label={`Standalone Expenses (${data.standalone_expenses.length})`}
          value={fmt(totalStandaloneExpenses)}
          note={qbExpensesCount ? `${qbExpensesCount} from QuickBooks` : undefined}
        />
        <SummaryCard
          label="Combined Outlays"
          value={fmt(combined)}
          emphasize
        />
      </div>

      {/* Bills */}
      <h2
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "#6B7280",
          margin: "20px 0 6px",
        }}
      >
        Bills
        {data.truncated_bills && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 9,
              color: "#92400E",
              background: "#FEF3C7",
              padding: "1px 6px",
              borderRadius: 3,
            }}
          >
            Showing first {data.bills.length} records — additional bills exist
          </span>
        )}
      </h2>

      {data.bills.length === 0 ? (
        <p style={{ fontSize: 11, color: "#9CA3AF", padding: "8px 0" }}>
          No bills recorded for this project.
        </p>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "14%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "26%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "15%" }} />
          </colgroup>
          <thead style={{ display: "table-header-group" }}>
            <tr>
              <th style={{ ...headCell, textAlign: "left" }}>Bill #</th>
              <th style={{ ...headCell, textAlign: "left" }}>Date</th>
              <th style={{ ...headCell, textAlign: "left" }}>Vendor</th>
              <th style={{ ...headCell, textAlign: "left" }}>Due</th>
              <th style={{ ...headCell, textAlign: "left" }}>Source</th>
              <th style={{ ...headCell, textAlign: "left" }}>Status</th>
              <th style={{ ...headCell, textAlign: "right" }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {billStatusOrder.map((statusKey) => {
              const rows = billsByStatus[statusKey];
              const subtotal = rows.reduce(
                (s, b) => s + Number(b.total || 0),
                0,
              );
              return (
                <React.Fragment key={statusKey}>
                  {rows.map((b) => (
                    <tr key={b.id} style={{ pageBreakInside: "avoid" }}>
                      <td
                        style={{
                          ...textCell,
                          fontFamily:
                            "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}
                      >
                        {b.bill_number}
                        {b.source === "quickbooks" && <QbBadge />}
                      </td>
                      <td style={textCell}>
                        {format(new Date(b.bill_date + "T00:00:00"), "MMM d, yyyy")}
                      </td>
                      <td style={textCell}>{b.vendor_name || "—"}</td>
                      <td style={textCell}>
                        {b.due_date
                          ? format(new Date(b.due_date + "T00:00:00"), "MMM d, yyyy")
                          : "—"}
                      </td>
                      <td style={{ ...textCell, color: "#6B7280" }}>
                        {b.source === "quickbooks" ? "QuickBooks" : "Manual"}
                      </td>
                      <td style={textCell}>
                        <StatusPill status={b.status} />
                      </td>
                      <td style={{ ...numCell, fontWeight: 600 }}>
                        {fmt(Number(b.total))}
                      </td>
                    </tr>
                  ))}
                  <tr style={{ pageBreakInside: "avoid" }}>
                    <td
                      colSpan={6}
                      style={{
                        padding: "5px 8px",
                        fontSize: 10,
                        textAlign: "right",
                        fontWeight: 600,
                        color: "#6B7280",
                        background: "#FAFAFA",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      Subtotal — {statusKey.replace(/_/g, " ")} ({rows.length})
                    </td>
                    <td
                      style={{
                        ...numCell,
                        fontWeight: 700,
                        background: "#FAFAFA",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      {fmt(subtotal)}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td
                colSpan={6}
                style={{
                  padding: "8px",
                  textAlign: "right",
                  fontSize: 11,
                  fontWeight: 700,
                  borderTop: "2px solid #111",
                }}
              >
                TOTAL BILLS
              </td>
              <td
                style={{
                  ...numCell,
                  fontSize: 12,
                  fontWeight: 800,
                  borderTop: "2px solid #111",
                  borderBottom: "none",
                }}
              >
                {fmt(totalBills)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}

      {/* Standalone Expenses */}
      <h2
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "#6B7280",
          margin: "28px 0 6px",
        }}
      >
        Standalone Expenses
        {data.truncated_expenses && (
          <span
            style={{
              marginLeft: 8,
              fontSize: 9,
              color: "#92400E",
              background: "#FEF3C7",
              padding: "1px 6px",
              borderRadius: 3,
            }}
          >
            Showing first {data.standalone_expenses.length} records — additional
            expenses exist
          </span>
        )}
      </h2>
      <p style={{ fontSize: 9, color: "#9CA3AF", margin: "0 0 6px" }}>
        Excludes expenses auto-generated from paid bills (those are listed under
        Bills above to prevent double-counting).
      </p>

      {data.standalone_expenses.length === 0 ? (
        <p style={{ fontSize: 11, color: "#9CA3AF", padding: "8px 0" }}>
          No standalone expenses for this project.
        </p>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "12%" }} />
            <col style={{ width: "37%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "13%" }} />
          </colgroup>
          <thead style={{ display: "table-header-group" }}>
            <tr>
              <th style={{ ...headCell, textAlign: "left" }}>Date</th>
              <th style={{ ...headCell, textAlign: "left" }}>Description</th>
              <th style={{ ...headCell, textAlign: "left" }}>Vendor</th>
              <th style={{ ...headCell, textAlign: "left" }}>Source</th>
              <th style={{ ...headCell, textAlign: "left" }}>Status</th>
              <th style={{ ...headCell, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.standalone_expenses.map((e) => (
              <tr key={e.id} style={{ pageBreakInside: "avoid" }}>
                <td style={textCell}>
                  {format(new Date(e.expense_date), "MMM d, yyyy")}
                </td>
                <td style={textCell}>
                  {e.description}
                  {e.qb_expense_id && <QbBadge />}
                </td>
                <td style={textCell}>{e.vendor_name || "—"}</td>
                <td style={{ ...textCell, color: "#6B7280" }}>
                  {e.qb_expense_id ? "QuickBooks" : "Manual"}
                </td>
                <td style={textCell}>
                  <StatusPill status={e.status} />
                </td>
                <td style={{ ...numCell, fontWeight: 600 }}>
                  {fmt(Number(e.amount))}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td
                colSpan={5}
                style={{
                  padding: 8,
                  textAlign: "right",
                  fontSize: 11,
                  fontWeight: 700,
                  borderTop: "2px solid #111",
                }}
              >
                TOTAL STANDALONE EXPENSES
              </td>
              <td
                style={{
                  ...numCell,
                  fontSize: 12,
                  fontWeight: 800,
                  borderTop: "2px solid #111",
                  borderBottom: "none",
                }}
              >
                {fmt(totalStandaloneExpenses)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}

      {/* Combined */}
      <div
        style={{
          marginTop: 24,
          padding: "12px 16px",
          background: "#111",
          color: "#fff",
          borderRadius: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          Combined Project Outlays
        </span>
        <span
          style={{
            fontSize: 16,
            fontWeight: 800,
            fontFamily:
              "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          {fmt(combined)}
        </span>
      </div>

      <CommandXFooter />
    </div>
  );
});

ProjectFinancialReportPrintTemplate.displayName =
  "ProjectFinancialReportPrintTemplate";

const SummaryCard = ({
  label,
  value,
  note,
  emphasize,
}: {
  label: string;
  value: string;
  note?: string;
  emphasize?: boolean;
}) => (
  <div
    style={{
      padding: 12,
      borderRadius: 4,
      border: emphasize ? "1px solid #111" : "1px solid #E5E7EB",
      background: emphasize ? "#F9FAFB" : "#fff",
    }}
  >
    <p
      style={{
        fontSize: 9,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "#6B7280",
        fontWeight: 600,
        margin: "0 0 4px",
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: 16,
        fontWeight: 800,
        margin: 0,
        fontFamily:
          "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
      }}
    >
      {value}
    </p>
    {note && (
      <p
        style={{
          fontSize: 9,
          color: "#6B7280",
          margin: "2px 0 0",
        }}
      >
        {note}
      </p>
    )}
  </div>
);
